import React, { useEffect, useState } from 'react'
import { DatePicker, message, Modal, Radio, Select, Spin, Statistic, Switch, Tooltip } from 'antd'
import { DashboardItemWrapper, DashboardSummaryItem, DashboardSummaryWrapper, ItemTitle } from '../../dashboard.styled'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import useParams from '../../../../hook/useParams'
import { fakeData, fakeSiteBrand, metrics } from './conf'
import { fetchSiteBrand, fetchSiteCharts, modSiteChartEnable } from '../../../../apis/dashboard'
import { useSelector, useDispatch } from 'react-redux'
import { ArrowDownOutlined, ArrowUpOutlined, QuestionCircleFilled, WarningFilled } from '@ant-design/icons'
import { Line } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { CHECKAUTH } from '../../../../constants/ActionTypes'

const resetDefaultParams = { maxResults: undefined, starIndex: undefined, sort: undefined }
function SiteChart (props) {
  const { useFakeData, settingMode } = props
  const { t: ln_t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const params = useParams({ ...resetDefaultParams, startDate: dayjs().add(-7, 'd'), endDate: dayjs().add(-1, 'd') }, false)
  const { fetchParams, handleFetchParams, setReady, ready } = params
  const [siteBrand, setSiteBrand] = useState([])
  const [allData, setAllData] = useState({})
  const [data, setData] = useState({})
  const [enableLoading, setEnableLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [warnIfEnable, setWarnIfEnable] = useState(false)
  const [selectedMetric, setSelectedMetric] = useState({ metric1: 'visits', metric2: undefined, average: false })
  const { metric1, metric2, average } = selectedMetric
  const isEnabled = user?.brands?.find(i => i.id === user?.nowBrand)?.siteChartEnable
  const showChart = (isEnabled && siteBrand.length) || settingMode

  const metricsOpt = Object.keys(metrics).map(i => ({ value: i, label: ln_t(`dashboard.${i}`) }))

  const chartOptions = {
    responsive: true,
    hoverBackgroundColor: 'red',
    plugins: { datalabels: { display: false } },
    interaction: { mode: 'index', intersect: true },
    stacked: false,
    tooltips: { mode: 'x-axis', intersect: true },
    scales: {
      yAxes: [
        { id: 'left', type: 'linear', position: 'left' },
        ...average || metric2 ? [{ id: 'right', type: 'linear', position: 'right', gridLines: { display: false } }] : []
      ]
    }
  }
  const chartData = {
    labels: data?.first?.map(i => i.date),
    datasets: [
      {
        label: ln_t(`dashboard.${metric1}`),
        data: data?.first?.map(i => i.value),
        borderColor: 'rgba(5, 141, 199)',
        backgroundColor: 'rgba(5, 141, 199, .2)',
        fill: 'origin',
        yAxisID: 'left',
        tension: 0
      },
      ...average || metric2
        ? [{
            label: ln_t(`dashboard.${average ? 'averageValue' : metric2}`),
            data: data?.second?.map(i => i.value),
            borderColor: 'rgba(237, 86, 27)',
            backgroundColor: 'rgba(237, 86, 27, .2)',
            fill: 'origin',
            yAxisID: 'right',
            tension: 0
          }]
        : []
    ]
  }

  const getMetricValue = (conf = {}, obj = {}) => {
    const { key, dividedBy } = conf
    return dividedBy ? Number(obj?.[key]) / Number(obj?.[dividedBy]) : Number(obj?.[key])
  }
  const getMetricData = (metric, data = []) => data?.map(i => ({ date: i['dm:date'], value: getMetricValue(metrics[metric], i) }))

  const handleChangeEnable = async enable => {
    if (warnIfEnable && enable) return Modal.info({ title: ln_t('dashboard.warning'), content: ln_t('message.error.cantEnableSiteChart'), icon: <WarningFilled style={{ color: '#FB1313' }} /> })
    setEnableLoading(true)
    const res = await modSiteChartEnable(user?.nowBrand, { siteChartEnable: enable ? 1 : 0 })
    if (res?.status === 200) {
      dispatch({ type: CHECKAUTH.REQUEST })
      message.success(ln_t('message.success.modify'))
    }
    setTimeout(() => setEnableLoading(false), 500)
  }
  const handleChangeAvg = (e, nowData = data) => {
    const showAvg = e.target.value
    setSelectedMetric({ ...selectedMetric, average: showAvg })
    if (!showAvg) return setData({ ...data, second: [] })
    const totalDays = fetchParams.endDate.diff(fetchParams.startDate, 'day') + 1
    const avgData = nowData?.first?.reduce((all, i) => all + i.value, 0) / totalDays
    const avgObj = nowData?.first?.map(i => ({ ...i, value: avgData }))
    setData({ ...nowData, second: avgObj })
  }

  const handleChangeData = (nowMetrics = selectedMetric, datas = allData, setupAverage) => {
    setSelectedMetric(nowMetrics)
    const { metric1, metric2 } = nowMetrics || {}
    const lastPeriod = metric2 === 'lastPeriod'
    const firstLineData = getMetricData(metric1, datas.thisPeriodByDate)
    const secondLineData = metric2 ? getMetricData(lastPeriod ? metric1 : metric2, lastPeriod ? datas.lastPeriodByDate : datas.thisPeriodByDate) : []
    const updateData = { first: firstLineData, second: secondLineData }
    setData(updateData)
    if (setupAverage) handleChangeAvg({ target: { value: average } }, updateData)
  }

  const getSiteBrand = async (doSetup) => {
    const res = await fetchSiteBrand(user?.nowGroup, user?.nowBrand)
    if (res?.status === 200 && doSetup) {
      setSiteBrand(res?.data?.map(i => ({ value: i.siteId, label: i.siteName })))
      handleFetchParams('add', { siteId: res?.data?.[0]?.siteId })
    }
    if (!res?.data?.length) setWarnIfEnable(true)
    setReady(true)
  }

  const getData = async () => {
    setLoading(true)
    const { startDate, endDate, ...other } = fetchParams
    const res = await fetchSiteCharts({ ...other, startDate: dayjs(startDate).format('YYYY-MM-DD'), endDate: dayjs(endDate).format('YYYY-MM-DD') })
    if (res?.status === 200) {
      setAllData(res?.data)
      handleChangeData(selectedMetric, res?.data, average)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (useFakeData || settingMode) {
      setAllData(fakeData)
      setSiteBrand(fakeSiteBrand)
      handleFetchParams('add', { siteId: fakeSiteBrand[0]?.value })
      handleChangeData(selectedMetric, fakeData)
    }
    getSiteBrand(!(useFakeData || settingMode))
  }, [])

  useEffect(() => { if (ready && showChart && !useFakeData && !settingMode) getData() }, [fetchParams])

  const roundedNumber = (num, decimal = 2) => parseFloat(num.toFixed(decimal))

  return showChart
    ? <DashboardItemWrapper style={{ marginTop: '24px', filter: isEnabled ? '' : 'grayscale(1)' }}>
      <ItemTitle>
        <div>
          {settingMode && <Switch defaultChecked checkedChildren={ln_t('util.enable')} unCheckedChildren={ln_t('util.disable')} checked={isEnabled} onChange={handleChangeEnable} loading={enableLoading} />}
          <div style={{ marginTop: '8px' }}>{ln_t('dashboard.dailyView')}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
          <Select placeholder={ln_t('util.pelaseSelect')} options={siteBrand} value={fetchParams?.siteId} onChange={e => handleFetchParams('add', { siteId: e })} />
          <DatePicker.RangePicker
            presets={[
              { label: ln_t('dashboard.yesterday'), value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')] },
              { label: ln_t('dashboard.lastWeek'), value: [dayjs().add(-1, 'w').startOf('week').add(1, 'd'), dayjs().add(-1, 'w').endOf('week').add(1, 'd')] },
              { label: ln_t('dashboard.lastMonth'), value: [dayjs().add(-1, 'M').startOf('month'), dayjs().add(-1, 'M').endOf('month')] }
            ]}
            needConfirm
            style={{ height: 'fit-content' }}
            value={[fetchParams.startDate, fetchParams.endDate]}
            onChange={([startDate, endDate]) => handleFetchParams('add', { startDate, endDate })}
            disabledDate={(current) => current && (current > dayjs().add(-1, 'd') || current > fetchParams.startDate.add(3, 'months'))}
          />
        </div>
      </ItemTitle>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '16px 0' }}>
        <Select
          placeholder={ln_t('dashboard.selectMetric')}
          options={metricsOpt?.map(i => ({ ...i, disabled: i.value === metric2 }))}
          style={{ minWidth: '220px' }}
          value={metric1}
          onChange={e => handleChangeData({ ...selectedMetric, metric1: e })}
        />
        vs
        <Select
          allowClear
          placeholder={ln_t('dashboard.selectMetric')}
          options={[{ value: 'lastPeriod', label: ln_t('dashboard.lastPeriod') }, ...metricsOpt?.map(i => ({ ...i, disabled: i.value === metric1 }))]}
          style={{ minWidth: '220px' }}
          value={metric2}
          onChange={e => handleChangeData({ ...selectedMetric, metric2: e })}
          disabled={average}
        />
        <div style={{ marginLeft: 'auto' }}>{ln_t('dashboard.averageValue')}</div>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          defaultValue={0}
          options={[{ value: 1, label: 'ON' }, { value: 0, label: 'OFF' }]}
          disabled={metric2}
          onChange={handleChangeAvg}
        />
      </div>

      <Spin style={{ padding: '8px 16px' }} spinning={loading}>
        <Line options={chartOptions} data={chartData} height={65} plugins={[ChartDataLabels]} />
      </Spin>

      <DashboardSummaryWrapper>
        {Object.entries(metrics).map(([metric, obj]) => {
          if (obj.noSummary) return <span key={metric}/>
          const { thisPeriodSummary, lastPeriodSummary } = allData || {}
          const thisPeriod = roundedNumber(getMetricValue(obj, thisPeriodSummary) * (obj.usePercentage ? 100 : 1))
          const lastPeriod = roundedNumber(getMetricValue(obj, lastPeriodSummary) * (obj.usePercentage ? 100 : 1))
          const percentage = roundedNumber((lastPeriod - thisPeriod) / lastPeriod * 100) || 0
          const { valueStyle, ...customPercentageProps } = percentage > 0 ? { valueStyle: { color: '#cf1322' }, prefix: <ArrowDownOutlined /> } : { valueStyle: { color: '#3f8600' }, prefix: <ArrowUpOutlined /> }
          const percentageProps = { value: Math.abs(percentage), suffix: '%', valueStyle: { fontSize: '14px', fontWeight: 700, ...valueStyle }, ...customPercentageProps }
          return <DashboardSummaryItem key={metric}>
            <div style={{ fontSize: '12px' }}>
              {ln_t(`dashboard.${metric}`)} <Tooltip title={ln_t(`dashboard.${metric}Desc`)}><QuestionCircleFilled /></Tooltip>
            </div>
            <Statistic value={thisPeriod || 0} valueStyle={{ fontSize: '20px', fontWeight: 700, color: '#4d9edc' }} style={{ margin: '8px 0 2px' }} suffix={obj.usePercentage && '%'} />
            <Statistic {...percentageProps} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: '4px 0' }}>
              {ln_t('dashboard.lastPeriod')} :
              <Statistic value={lastPeriod || 0} valueStyle={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0,0,0,.5)', lineHeight: '16px' }} style={{ margin: '0' }} suffix={obj.usePercentage && '%'} />
            </div>
          </DashboardSummaryItem>
        })}
      </DashboardSummaryWrapper>
    </DashboardItemWrapper>
    : ''
}

export default SiteChart
